<template>
  <q-dialog
    :model-value="modelValue"
    maximized
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card>
      <!-- Header -->
      <q-card-section class="bg-primary text-white">
        <div class="row items-center">
          <q-icon name="description" size="md" class="q-mr-md" />
          <div class="text-h5">Generate Tender Letter</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </div>
      </q-card-section>

      <!-- Stepper -->
      <q-stepper
        v-model="step"
        vertical
        color="primary"
        animated
        header-nav
      >
        <q-step :name="1" title="Letter Type" icon="settings">
          <q-option-group
            v-model="letterType"
            :options="[
              { label: 'Draft (Preview only, watermark applied)', value: 'draft' },
              { label: 'Send Quotation', value: 'send' }
            ]"
          />
          <div class="q-mt-md">
            <q-checkbox v-model="showPrices" label="Show prices" />
            <div class="text-caption text-grey-7 q-ml-lg">
              Uncheck to produce a client copy with no pricing or BOM prices.
            </div>
          </div>
          <q-stepper-navigation>
            <q-btn color="primary" label="Continue" @click="onStepOneContinue" />
          </q-stepper-navigation>
        </q-step>

        <q-step :name="2" title="Select Recipients" icon="people" :disable="letterType === 'draft'">
          <p>Select the contractors to receive this quotation:</p>

          <div class="q-mb-md">
            <q-btn
              flat
              dense
              label="Select All"
              color="primary"
              class="q-mr-sm"
              @click="selectAllContractors"
            />
            <q-btn
              flat
              dense
              label="Clear All"
              @click="selectedContractorContacts = []"
            />
          </div>

          <q-list bordered separator>
            <q-item
              v-for="option in contractorContactOptions"
              :key="`${option.customerId}-${option.contactId}`"
              tag="label"
              :disable="!option.email"
            >
              <q-item-section avatar>
                <q-checkbox
                  v-model="selectedContractorContacts"
                  :val="option"
                  :disable="!option.email"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ option.customerName }}</q-item-label>
                <q-item-label caption>
                  {{ option.contactName }} - {{ option.email || 'No email address' }}
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-badge v-if="!option.email" color="warning" label="No email" />
              </q-item-section>
            </q-item>
          </q-list>

          <q-banner v-if="contractorContactOptions.length === 0" class="bg-warning text-white q-mt-md">
            <template #avatar>
              <q-icon name="warning" />
            </template>
            No contractors with contacts found. Please assign contractors with contacts to this tender first.
          </q-banner>

          <q-stepper-navigation>
            <q-btn
              color="primary"
              label="Preview"
              :disable="selectedContractorContacts.length === 0"
              @click="step = 3"
            />
            <q-btn flat label="Back" class="q-ml-sm" @click="step = 1" />
          </q-stepper-navigation>
        </q-step>

        <q-step :name="3" title="Preview & Send" icon="send">
          <div class="q-mb-md">
            <q-btn
              color="accent"
              label="Preview PDF"
              icon="visibility"
              @click="openPdfPreview"
            />
            <q-btn
              outline
              color="primary"
              label="Download as Word"
              icon="mdi-file-word"
              class="q-ml-sm"
              @click="downloadWord"
            />
          </div>

          <q-banner v-if="letterType === 'draft'" class="bg-orange-2">
            <template #avatar>
              <q-icon name="info" color="orange" />
            </template>
            This is a DRAFT preview with watermark. No emails will be sent.
          </q-banner>

          <q-banner v-if="letterType === 'send'" class="bg-positive text-white">
            <template #avatar>
              <q-icon name="send" />
            </template>
            <div>
              <strong>Quotation will be sent to {{ selectedContractorContacts.length }} recipient(s):</strong>
              <ul class="q-mb-none q-mt-sm">
                <li v-for="contact in selectedContractorContacts" :key="`${contact.customerId}-${contact.contactId}`">
                  {{ contact.contactName }} at {{ contact.customerName }} ({{ contact.email }})
                </li>
              </ul>
              <small class="q-mt-sm block">Includes: Bar-Tech branded email with PDF attachment</small>
            </div>
          </q-banner>

          <q-stepper-navigation>
            <q-btn
              color="primary"
              :label="getSendButtonLabel"
              :loading="generating"
              @click="generate"
            />
            <q-btn flat label="Back" class="q-ml-sm" @click="step = letterType === 'draft' ? 1 : 2" />
          </q-stepper-navigation>
        </q-step>

        <q-step :name="4" title="Complete" icon="check_circle">
          <div class="text-center q-pa-lg">
            <q-icon name="check_circle" color="positive" size="64px" />
            <div class="text-h6 q-mt-md">{{ getCompleteTitle }}</div>
            <p v-if="letterType === 'send'">
              Quotation sent to {{ selectedContractorContacts.length }} recipient(s).
            </p>
          </div>
          <q-stepper-navigation>
            <q-btn color="primary" label="Done" @click="closeDialog" />
            <q-btn v-if="letterType === 'draft'" flat label="Download PDF" icon="download" class="q-ml-sm" @click="downloadPdf" />
          </q-stepper-navigation>
        </q-step>
      </q-stepper>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';

  const props = defineProps({
    modelValue: Boolean,
    tenderId: { type: Number, required: true },
  });

  const emit = defineEmits(['update:modelValue', 'letter-generated']);

  const $q = useQuasar();

  const step = ref(1);
  const letterType = ref('draft');
  const showPrices = ref(true);
  const contractors = ref([]);
  const selectedContractorContacts = ref([]);
  const generating = ref(false);
  const generatedPdfUrl = ref(null);
  const tenderData = ref(null);

  // Computed - Build contact options from contractors already on the tender
  const contractorContactOptions = computed(() => {
    const options = [];
    for (const contractor of contractors.value) {
      const customerName = contractor.company_name || contractor.business_name || contractor.name;
      // Get contacts from the contractor
      const contacts = contractor.contacts || [];
      for (const contact of contacts) {
        options.push({
          customerName,
          customerId: contractor.contractor_id,
          contactId: contact.id,
          contactName: contact.name,
          email: contact.email,
        });
      }
      // If no contacts but contractor has direct email, add that
      if (contacts.length === 0 && contractor.email) {
        options.push({
          customerName,
          customerId: contractor.contractor_id,
          contactId: null,
          contactName: contractor.contact_name || 'Primary Contact',
          email: contractor.email,
        });
      }
    }
    return options;
  });

  const getSendButtonLabel = computed(() => {
    if (letterType.value === 'draft') {return 'Generate Draft';}
    return `Send to ${selectedContractorContacts.value.length} Recipient(s)`;
  });

  const getCompleteTitle = computed(() => {
    if (letterType.value === 'draft') {return 'Draft Generated!';}
    return 'Quotation Sent!';
  });

  onMounted(async () => {
    // Load tender data with contractors
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}`);
      const data = await response.json();
      tenderData.value = data.data;
      contractors.value = data.data.contractors || [];
    } catch (error) {
      console.error('Failed to load tender data:', error);
    }
  });

  function selectAllContractors() {
    selectedContractorContacts.value = contractorContactOptions.value.filter((opt) => opt.email);
  }

  function onStepOneContinue() {
    if (letterType.value === 'draft') {
      step.value = 3; // Skip contact selection for drafts
    } else {
      // Check we have contractors with contacts
      if (contractorContactOptions.value.length === 0) {
        $q.notify({
          type: 'warning',
          message: 'No contractors with contacts assigned to this tender. Please add contractors first.',
        });
        return;
      }
      step.value = 2;
    }
  }

  async function openPdfPreview() {
    const isDraft = letterType.value === 'draft' ? 1 : 0;
    const token = sessionStorage.getItem('token') || localStorage.getItem('auth_token');

    try {
      // Call the generate endpoint to create/get the PDF
      const response = await fetch(`/api/tenders/${props.tenderId}/generate-letter?draft=${isDraft}&show_prices=${showPrices.value ? 1 : 0}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success && result.data && result.data.pdf_url) {
        // Open the PDF URL in a new window with token for authentication
        const pdfUrl = `${result.data.pdf_url}?token=${token}`;
        window.open(pdfUrl, '_blank');
      } else {
        $q.notify({
          type: 'negative',
          message: result.message || 'Failed to generate PDF preview',
        });
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to generate PDF preview',
      });
    }
  }

  async function downloadWord() {
    const isDraft = letterType.value === 'draft';
    const token = sessionStorage.getItem('token') || localStorage.getItem('auth_token');

    $q.notify({ type: 'positive', message: 'Generating Word document...' });

    window.open(`/api/tenders/${props.tenderId}/generate-letter-word?draft=${isDraft ? 1 : 0}&show_prices=${showPrices.value ? 1 : 0}&token=${token}`, '_blank');
  }

  async function generate() {
    generating.value = true;

    try {
      // Determine endpoint and payload based on letter type
      const isDraft = letterType.value === 'draft';
      const endpoint = isDraft
        ? `/api/tenders/${props.tenderId}/generate-draft`
        : `/api/tenders/${props.tenderId}/send-final`;

      // Build contractor_ids from selected contacts
      const contractorIds = selectedContractorContacts.value.map((c) => c.customerId);

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ contractor_ids: contractorIds, show_prices: showPrices.value ? 1 : 0 }),
      });
      const data = await response.json();

      if (data.success && data.data) {
        const generation = data.data;
        const token = sessionStorage.getItem('token') || localStorage.getItem('auth_token');
        generatedPdfUrl.value = `${generation.pdf_url}?token=${token}`;

        step.value = 4;

        if (letterType.value === 'draft') {
          $q.notify({ type: 'positive', message: 'Draft generated successfully' });
          window.open(generatedPdfUrl.value, '_blank');
        } else {
          $q.notify({
            type: 'positive',
            message: `Quotation sent to ${selectedContractorContacts.value.length} recipient(s)`,
          });
          emit('letter-generated');
        }
      } else {
        throw new Error(data.message || 'Failed to generate letter');
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({ type: 'negative', message: 'Failed: ' + error.message });
      }
    } finally {
      generating.value = false;
    }
  }

  function downloadPdf() {
    if (generatedPdfUrl.value) {
      const token = sessionStorage.getItem('token') || localStorage.getItem('auth_token');
      window.open(`${generatedPdfUrl.value}&token=${token}`, '_blank');
    }
  }

  function closeDialog() {
    step.value = 1;
    letterType.value = 'draft';
    showPrices.value = true;
    selectedContractorContacts.value = [];
    emit('update:modelValue', false);
  }
</script>
